import { useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  partnerApi,
  type SchemaLookupToolDefinitionInput,
  type ToolLookupConfigInput,
} from '../../lib/api';
import { LookupFilterEditor } from './LookupFilterEditor';
import {
  LOOKUP_MAX_LIMIT,
  buildLookupSchemaSet,
  lookupFilterValueMatchesField,
  type LookupSchemaSetup,
} from './lookup-tool-model';
import { LookupReviewSummary } from './LookupReviewSummary';
import { LookupSchemaSetupDialog } from './LookupSchemaSetupDialog';
import { LookupSchemaSummary } from './LookupSchemaSummary';
import { validateToolName } from './tool-inputs';
import { ToolFormField as Field } from './ToolFormField';
import { ToolFormSection as Section } from './ToolFormSection';
import styles from './LookupTool.module.css';
import formStyles from './ToolForm.module.css';

interface LookupToolFormProps {
  draft: SchemaLookupToolDefinitionInput;
  onChange: (draft: SchemaLookupToolDefinitionInput) => void;
  isEditing?: boolean;
  canonicalName?: string;
  storedConfig?: ToolLookupConfigInput;
  onValidityChange?: (isValid: boolean) => void;
  onStaleConfigurationChange?: (isStale: boolean) => void;
}

/** Names why a saved lookup needs attention, so the disabled Save button is never a dead end. */
const staleReason = ({
  setup,
  filtersReferenceMissingFields,
  filtersHaveInvalidValues,
}: {
  setup: LookupSchemaSetup | undefined;
  filtersReferenceMissingFields: boolean;
  filtersHaveInvalidValues: boolean;
}): string => {
  if (filtersReferenceMissingFields) {
    return 'One or more fixed filters point at a schema field that no longer exists. Repoint or remove them below, then save.';
  }
  if (filtersHaveInvalidValues) {
    return 'One or more fixed filter values no longer match the schema. Enter a compatible value below, then save.';
  }
  return (
    setup?.reasons.join(' ') ||
    'The saved schema is no longer available. Choose a replacement or set only the status to inactive.'
  );
};

export const LookupToolForm = ({
  draft,
  onChange,
  isEditing = false,
  canonicalName,
  storedConfig,
  onValidityChange,
  onStaleConfigurationChange,
}: LookupToolFormProps) => {
  const [mappingSetup, setMappingSetup] = useState<LookupSchemaSetup | null>(null);
  const schemasQuery = useQuery({
    queryKey: ['schemaDefinitions'],
    queryFn: () => partnerApi.getSchemaDefinitions(),
  });
  const mappingsQuery = useQuery({
    queryKey: ['schemaMappings'],
    queryFn: () => partnerApi.getSchemaMappings(),
  });

  const schemaSetupAvailable = schemasQuery.data !== undefined && mappingsQuery.data !== undefined;
  const schemaSetupLoadFailed = schemasQuery.isError || mappingsQuery.isError;
  const schemaSetupIsFetching = schemasQuery.isFetching || mappingsQuery.isFetching;
  const setups = schemaSetupAvailable
    ? buildLookupSchemaSet({
        schemas: schemasQuery.data,
        mappings: mappingsQuery.data,
        storedConfig,
      })
    : [];
  const eligibleSetups = setups.filter(setup => setup.isEligible);
  const setupCandidates = setups.filter(
    setup =>
      !setup.isEligible &&
      setup.schema?.category === 'custom_schema' &&
      !setup.schema.deactivatedAt &&
      !setup.schema.deactivated_at,
  );
  const selectedSetup = setups.find(setup => setup.id === draft.lookup_config.schema_definition_id);
  const selectedIsUnavailable = Boolean(selectedSetup && !selectedSetup.isEligible);

  const nameError = isEditing ? null : validateToolName(draft.display_name);
  const descriptionValid = draft.description.trim().length > 0;
  const limitValid =
    Number.isInteger(draft.lookup_config.limit) &&
    draft.lookup_config.limit >= 1 &&
    draft.lookup_config.limit <= LOOKUP_MAX_LIMIT;
  const filtersUnique =
    new Set(draft.lookup_config.filters.map(filter => filter.field)).size ===
    draft.lookup_config.filters.length;
  const filtersReferenceMissingFields = draft.lookup_config.filters.some(
    filter => !selectedSetup?.filterFields.some(field => field.path === filter.field),
  );
  const filtersHaveInvalidValues = draft.lookup_config.filters.some(filter => {
    const field = selectedSetup?.filterFields.find(candidate => candidate.path === filter.field);
    return field ? !lookupFilterValueMatchesField(field, filter.value) : false;
  });
  const isValid =
    !nameError &&
    descriptionValid &&
    Boolean(selectedSetup?.isEligible) &&
    limitValid &&
    filtersUnique &&
    !filtersReferenceMissingFields &&
    !filtersHaveInvalidValues;
  const isStale =
    schemaSetupAvailable &&
    (selectedIsUnavailable ||
      Boolean(storedConfig && !selectedSetup) ||
      (Boolean(storedConfig) && (filtersReferenceMissingFields || filtersHaveInvalidValues)));

  // The parent owns save-button state; report derived validity after each controlled draft/query change.
  useEffect(() => {
    onValidityChange?.(isValid);
  }, [isValid, onValidityChange]);

  // Staleness changes patch semantics: a valid repair must re-send lookupConfig so it revalidates.
  useEffect(() => {
    onStaleConfigurationChange?.(isStale);
  }, [isStale, onStaleConfigurationChange]);

  const set = <K extends keyof SchemaLookupToolDefinitionInput>(
    key: K,
    value: SchemaLookupToolDefinitionInput[K],
  ) => onChange({ ...draft, [key]: value });
  const setLookupConfig = (lookupConfig: SchemaLookupToolDefinitionInput['lookup_config']) =>
    set('lookup_config', lookupConfig);

  const selectSchema = (value: string) => {
    const setupPrefix = 'setup:';
    if (value.startsWith(setupPrefix)) {
      const setup = setupCandidates.find(candidate => candidate.id === value.slice(setupPrefix.length));
      if (setup) setMappingSetup(setup);
      return;
    }
    setLookupConfig({
      schema_definition_id: value,
      filters: [],
      limit: draft.lookup_config.limit,
    });
  };
  const setupGroupLabel = eligibleSetups.length > 0 ? 'Set up another schema' : 'Set up a schema';
  const staleMessage = isStale
    ? staleReason({
        setup: selectedSetup,
        filtersReferenceMissingFields,
        filtersHaveInvalidValues,
      })
    : null;

  return (
    <div className={formStyles.form}>
      <Section
        step={1}
        title="Name and describe the tool"
        subtitle="The agent reads the description to decide when to call this tool.">
        {isEditing && canonicalName ? (
          <Field label="Tool name" hint="The runtime name agents call. Locked after creation.">
            <input
              aria-label="Tool name"
              type="text"
              value={canonicalName}
              readOnly
              className={formStyles.monoInput}
            />
          </Field>
        ) : (
          <Field
            label="Name"
            hint="Lowercase snake_case, starting with a letter (e.g. lookup_service_locations).">
            <input
              aria-label="Name"
              type="text"
              value={draft.display_name}
              onChange={event => set('display_name', event.target.value)}
              disabled={isEditing}
              className={formStyles.monoInput}
              placeholder="lookup_service_locations"
              aria-invalid={Boolean(nameError)}
            />
            {nameError && <span className={formStyles.fieldError}>{nameError}</span>}
          </Field>
        )}
        <Field label="Description" hint="Tell the agent what this finds and when to use it.">
          <textarea
            aria-label="Description"
            value={draft.description}
            onChange={event => set('description', event.target.value)}
            className={formStyles.textarea}
            rows={8}
            placeholder="Find service locations for the current customer."
          />
        </Field>
      </Section>

      <Section
        step={2}
        title="Choose the synced schema"
        subtitle="Choose the type of synced record this tool should search.">
        <Field
          label="Schema"
          hint="A schema is a type of synced record, such as a service location or work order.">
          <select
            aria-label="Schema"
            value={draft.lookup_config.schema_definition_id}
            className={formStyles.select}
            disabled={!schemaSetupAvailable}
            onChange={event => selectSchema(event.target.value)}>
            <option value="">Choose a schema…</option>
            {selectedSetup && selectedIsUnavailable && (
              <option value={selectedSetup.id} disabled>
                {selectedSetup.name} (saved configuration needs review)
              </option>
            )}
            {eligibleSetups.length > 0 && (
              <optgroup label="Ready to use">
                {eligibleSetups.map(setup => (
                  <option key={setup.id} value={setup.id}>
                    {setup.name}
                  </option>
                ))}
              </optgroup>
            )}
            {setupCandidates.length > 0 && (
              <optgroup label={setupGroupLabel}>
                {setupCandidates.map(setup => (
                  <option key={setup.id} value={`setup:${setup.id}`}>
                    {setup.name}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        </Field>
        {!schemaSetupAvailable && !schemaSetupLoadFailed && schemaSetupIsFetching && (
          <p className={formStyles.helpText}>Loading schema setup…</p>
        )}
        {schemaSetupLoadFailed && (
          <aside className={styles.schemaLoadError} role="alert">
            <div>
              <strong>Schema setup could not be loaded</strong>
              <p>Try again before choosing or reviewing a synced schema.</p>
            </div>
            <button
              type="button"
              disabled={schemaSetupIsFetching}
              onClick={() => {
                void Promise.all([schemasQuery.refetch(), mappingsQuery.refetch()]);
              }}>
              {schemaSetupIsFetching ? 'Trying again…' : 'Try again'}
            </button>
          </aside>
        )}
        {staleMessage && (
          <aside className={styles.staleWarning}>
            <AlertTriangle />
            <div>
              <strong>Saved lookup configuration needs review</strong>
              <p>{staleMessage}</p>
            </div>
          </aside>
        )}
        {selectedSetup?.schema && (
          <LookupSchemaSummary setup={selectedSetup} onEdit={() => setMappingSetup(selectedSetup)} />
        )}
      </Section>

      <Section
        step={3}
        title="Configure the lookup"
        subtitle="Add fixed filters and cap how many records the tool can return.">
        <LookupFilterEditor
          fields={selectedSetup?.filterFields ?? []}
          filters={draft.lookup_config.filters}
          onChange={filters => setLookupConfig({ ...draft.lookup_config, filters })}
        />
        <Field label="Maximum records" hint={`Enter a whole number from 1 to ${LOOKUP_MAX_LIMIT}.`}>
          <input
            aria-label="Maximum records"
            type="number"
            min={1}
            max={LOOKUP_MAX_LIMIT}
            step={1}
            value={draft.lookup_config.limit}
            className={formStyles.monoInput}
            onChange={event =>
              setLookupConfig({
                ...draft.lookup_config,
                limit: Number(event.target.value),
              })
            }
            aria-invalid={!limitValid}
          />
        </Field>
      </Section>

      <Section
        step={4}
        title="Review the lookup"
        subtitle="Confirm the customer relationship and data returned to the agent.">
        {selectedSetup ? (
          <LookupReviewSummary setup={selectedSetup} draft={draft} />
        ) : (
          <p className={styles.reviewEmpty}>Choose an eligible schema to review the lookup.</p>
        )}
        <p className={styles.annotationSummary}>
          Agentic Tools lookups are always read-only, non-destructive, idempotent, and limited to the
          synced data configured here.
        </p>
      </Section>

      {isEditing && (
        <Section title="Status" subtitle="Inactive tools are hidden from agents.">
          <Field label="Status">
            <select
              aria-label="Status"
              value={draft.status}
              onChange={event => set('status', event.target.value as 'active' | 'inactive')}
              className={formStyles.select}>
              <option value="active">active</option>
              <option value="inactive">inactive</option>
            </select>
          </Field>
        </Section>
      )}
      {mappingSetup?.schema && mappingsQuery.data && (
        <LookupSchemaSetupDialog
          schema={mappingSetup.schema}
          mappings={mappingsQuery.data}
          onClose={() => setMappingSetup(null)}
          onSaved={() => {
            setLookupConfig({
              schema_definition_id: mappingSetup.id,
              filters: [],
              limit: draft.lookup_config.limit,
            });
            setMappingSetup(null);
          }}
        />
      )}
    </div>
  );
};
